import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  casePaymentInputSchema,
  type CasePaymentDto,
  type CasePaymentInput,
  type CasePaymentsSummary,
} from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { formatDate, formatMoney, todayInputValue } from '../../lib/format'
import { qk } from '../../lib/queryClient'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../common/Feedback'
import { DateField } from '../common/fields'

type FormValues = z.input<typeof casePaymentInputSchema>

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color?: string
}) {
  return (
    <Grid size={{ xs: 12, sm: 4 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color }}>
          {value}
        </Typography>
      </Paper>
    </Grid>
  )
}

function AddPaymentDialog({
  caseId,
  open,
  onClose,
}: {
  caseId: string
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const form = useForm<FormValues, unknown, CasePaymentInput>({
    resolver: formResolver(casePaymentInputSchema),
    defaultValues: { amount: '', paidAt: todayInputValue(), method: '', note: '' },
  })

  useEffect(() => {
    if (open) form.reset({ amount: '', paidAt: todayInputValue(), method: '', note: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const save = useMutation({
    mutationFn: (values: CasePaymentInput) =>
      api.post<CasePaymentsSummary>(`/cases/${caseId}/payments`, values),
    onSuccess: (summary) => {
      queryClient.setQueryData(qk.casePayments(caseId), summary)
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>תשלום חדש</DialogTitle>
        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              type="number"
              label="סכום (₪)"
              required
              autoFocus
              {...form.register('amount')}
              error={Boolean(errors.amount)}
              helperText={errors.amount?.message}
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
            />
            <DateField control={form.control} name="paidAt" label="תאריך התשלום" />
            <TextField
              label="אופן התשלום"
              placeholder="העברה בנקאית, מזומן, צ׳ק…"
              {...form.register('method')}
            />
            <TextField label="הערה" multiline minRows={2} {...form.register('note')} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {save.isPending ? 'שומר…' : 'שמירה'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/** The fee, what was received, and the open balance — with one row per payment. */
export function CasePaymentsPanel({ caseId }: { caseId: string }) {
  const queryClient = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [toDelete, setToDelete] = useState<CasePaymentDto | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.casePayments(caseId),
    queryFn: () => api.get<CasePaymentsSummary>(`/cases/${caseId}/payments`),
  })

  const remove = useMutation({
    mutationFn: (paymentId: string) =>
      api.del<CasePaymentsSummary>(`/cases/${caseId}/payments/${paymentId}`),
    onSuccess: (summary) => {
      queryClient.setQueryData(qk.casePayments(caseId), summary)
      setToDelete(null)
    },
  })

  if (isPending) return <LoadingBlock />
  if (error) return <ErrorAlert error={error} onRetry={() => void refetch()} />
  if (!data) return null

  const balanceColor =
    data.balance === null ? undefined : data.balance > 0 ? 'error.main' : 'success.main'

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <SummaryCard label="שכר טרחה" value={formatMoney(data.feeAmount)} />
        <SummaryCard label="שולם עד כה" value={formatMoney(data.totalPaid)} />
        <SummaryCard
          label="יתרה לתשלום"
          value={data.balance === null ? '—' : formatMoney(data.balance)}
          color={balanceColor}
        />
      </Grid>

      {data.feeAmount === null ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          שכר הטרחה נקבע בעריכת התיק; בלעדיו אין ממה לחשב יתרה.
        </Typography>
      ) : null}

      {remove.error ? <ErrorAlert error={remove.error} /> : null}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          תשלום חדש
        </Button>
      </Stack>

      {data.payments.length === 0 ? (
        <EmptyState title="עוד לא נרשמו תשלומים" hint="כל תשלום שמתקבל במשרד נרשם כאן" />
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>תאריך</TableCell>
                <TableCell>סכום</TableCell>
                <TableCell>אופן התשלום</TableCell>
                <TableCell>הערה</TableCell>
                <TableCell>נרשם על ידי</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{formatDate(payment.paidAt)}</TableCell>
                  <TableCell>{formatMoney(payment.amount)}</TableCell>
                  <TableCell>{payment.method || '—'}</TableCell>
                  <TableCell>{payment.note || '—'}</TableCell>
                  <TableCell>{payment.createdByName ?? '—'}</TableCell>
                  <TableCell align="left">
                    <Button size="small" color="error" onClick={() => setToDelete(payment)}>
                      מחיקה
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AddPaymentDialog caseId={caseId} open={addOpen} onClose={() => setAddOpen(false)} />

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="מחיקת תשלום"
        message={`התשלום על סך ${formatMoney(toDelete?.amount ?? 0)} מ־${formatDate(toDelete?.paidAt)} יימחק לצמיתות. להמשיך?`}
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete.id)}
        onClose={() => setToDelete(null)}
      />
    </>
  )
}
